import React, { useEffect, useState } from 'react'
import { Button, Checkbox, Input, Modal, Select } from 'antd'
import styled from 'styled-components/macro'
import { CHEF, ROLE_OPTIONS } from 'src/utils/roles'
import {
  MAX_CUSTOM_ROLE_NAME_LENGTH,
  RESERVED_CUSTOM_ROLE_NAME_HINT,
  VENUE_ROLE_CAPABILITY_KEYS,
  VENUE_ROLE_CAPABILITY_LABELS,
  getBasedOnLabel,
  getCapabilitiesFromTemplate,
  SYSTEM_ROLE_PICKER_DESCRIPTIONS,
  getCustomRoleNameErrorMessage,
  sanitizeVenueRoleCapabilities,
  validateCustomRoleName,
  CUSTOM_ROLE_EDIT_START_FROM_HINT,
} from 'src/utils/venueRoleCapabilities'

const CreateCustomRoleModal = ({
  visible,
  role = null,
  existingRoles = [],
  saving = false,
  isFreePlan = false,
  error = '',
  onCancel,
  onSave,
  onPaywall,
}) => {
  const [name, setName] = useState('')
  const [basedOn, setBasedOn] = useState(CHEF)
  const [capabilities, setCapabilities] = useState(
    getCapabilitiesFromTemplate(CHEF),
  )
  const [nameError, setNameError] = useState('')
  const isEditing = Boolean(role?.id)

  useEffect(() => {
    if (!visible) return
    if (role?.id) {
      setName(role.name || '')
      setBasedOn(role.basedOn || CHEF)
      setCapabilities(sanitizeVenueRoleCapabilities(role.capabilities))
    } else {
      setName('')
      setBasedOn(CHEF)
      setCapabilities(getCapabilitiesFromTemplate(CHEF))
    }
    setNameError('')
  }, [visible, role])

  const handleTemplateChange = nextBasedOn => {
    setBasedOn(nextBasedOn)
    if (isEditing) return
    setCapabilities(getCapabilitiesFromTemplate(nextBasedOn))
  }

  const handleTickChange = (key, checked) => {
    setCapabilities(current => ({
      ...sanitizeVenueRoleCapabilities(current),
      [key]: Boolean(checked),
    }))
  }

  const handleSave = () => {
    if (isFreePlan) {
      onPaywall?.({ name: String(name || '').trim() || null })
      return
    }
    const nameResult = validateCustomRoleName(name, {
      existingRoles,
      excludeId: role?.id,
    })
    if (!nameResult.ok) {
      setNameError(getCustomRoleNameErrorMessage(nameResult.error))
      return
    }
    setNameError('')
    onSave({
      name: nameResult.name,
      basedOn,
      capabilities: sanitizeVenueRoleCapabilities(capabilities),
    })
  }

  return (
    <Modal
      title={isEditing ? 'Edit custom role' : 'Create custom role'}
      visible={visible}
      onCancel={onCancel}
      destroyOnClose
      footer={[
        <Button key='cancel' onClick={onCancel} disabled={saving}>
          Cancel
        </Button>,
        <Button key='save' type='primary' onClick={handleSave} loading={saving}>
          Save
        </Button>,
      ]}
    >
      <Field>
        <Label htmlFor='custom-role-name'>Role name</Label>
        <Input
          id='custom-role-name'
          value={name}
          maxLength={MAX_CUSTOM_ROLE_NAME_LENGTH}
          placeholder='e.g. Kitchen Supervisor'
          onChange={event => {
            setName(event.target.value)
            if (nameError) setNameError('')
          }}
        />
        <NameCount data-role-name-count>
          {name.length}/{MAX_CUSTOM_ROLE_NAME_LENGTH}
        </NameCount>
        <Hint>{RESERVED_CUSTOM_ROLE_NAME_HINT}</Hint>
        {nameError ? <ErrorText>{nameError}</ErrorText> : null}
      </Field>

      <Field>
        <Label htmlFor='custom-role-based-on'>Start from</Label>
        <Select
          id='custom-role-based-on'
          value={basedOn}
          onChange={handleTemplateChange}
          style={{ width: '100%' }}
          optionLabelProp='label'
          dropdownMatchSelectWidth={false}
          getPopupContainer={() => document.body}
        >
          {ROLE_OPTIONS.map(option => (
            <Select.Option
              key={option.value}
              value={option.value}
              label={option.label}
            >
              {option.label}
              <span style={{ color: '#8c8c8c' }}>
                {' '}
                {SYSTEM_ROLE_PICKER_DESCRIPTIONS[option.value]}
              </span>
            </Select.Option>
          ))}
        </Select>
        <BasedOn>{getBasedOnLabel(basedOn)}</BasedOn>
        {isEditing ? <Hint>{CUSTOM_ROLE_EDIT_START_FROM_HINT}</Hint> : null}
      </Field>

      <Field>
        <Label>Capabilities</Label>
        <Hint>
          User management is not a tick — only Admins can manage users.
        </Hint>
        <Ticks>
          {VENUE_ROLE_CAPABILITY_KEYS.map(key => (
            <Checkbox
              key={key}
              checked={Boolean(capabilities[key])}
              onChange={event => handleTickChange(key, event.target.checked)}
            >
              {VENUE_ROLE_CAPABILITY_LABELS[key]}
            </Checkbox>
          ))}
        </Ticks>
      </Field>

      {error ? <ErrorText>{error}</ErrorText> : null}
    </Modal>
  )
}

const Field = styled.div`
  margin-bottom: 16px;
`

const Label = styled.label`
  display: block;
  font-weight: 600;
  font-size: 14px;
  color: #262626;
  margin-bottom: 6px;
`

const Hint = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: #8c8c8c;
`

const NameCount = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: #8c8c8c;
  text-align: right;
`

const BasedOn = styled.div`
  margin-top: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #595959;
`

const Ticks = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 16px;
  margin-top: 8px;
`

const ErrorText = styled.div`
  margin-top: 6px;
  font-size: 13px;
  color: #cf1322;
`

export default CreateCustomRoleModal
